/*
 * NorthernHour custom scripts
 * ----------------------------
 * All custom JS for the NorthernHour redesign lives here so the theme
 * stays upgradable.
 */

// Full-screen logo splash (snippets/nh-splash.liquid): runs immediately
// (not gated on DOMContentLoaded) so the splash starts its hold timer as
// early as possible. Respects prefers-reduced-motion (instant removal, no
// fade), the configured show-frequency, and falls back to the normal
// hold/fade sequence even if the GIF fails to load — the splash never traps
// the user.
document.querySelectorAll('[data-nh-splash]').forEach((splash) => {
  const remove = () => {
    splash.style.pointerEvents = 'none';
    splash.remove();
  };

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    remove();
    return;
  }

  const frequency = splash.dataset.nhSplashFrequency;
  const holdDuration = (Number(splash.dataset.nhSplashHold) || 2.5) * 1000;
  const fadeDuration = (Number(splash.dataset.nhSplashFade) || 1.2) * 1000;
  const storageKey = 'nh-splash-shown';

  let alreadyShown = false;
  if (frequency === 'once_per_session') {
    alreadyShown = sessionStorage.getItem(storageKey) === 'true';
  } else if (frequency === 'once_per_visitor') {
    alreadyShown = localStorage.getItem(storageKey) === 'true';
  }

  if (alreadyShown) {
    remove();
    return;
  }

  if (frequency === 'once_per_session') {
    sessionStorage.setItem(storageKey, 'true');
  } else if (frequency === 'once_per_visitor') {
    localStorage.setItem(storageKey, 'true');
  }

  const fadeOutAndRemove = () => {
    setTimeout(() => {
      splash.classList.add('nh-splash--hidden');
      splash.style.pointerEvents = 'none';
      setTimeout(remove, fadeDuration);
    }, holdDuration);
  };

  const logo = splash.querySelector('[data-nh-splash-logo]');
  if (logo && !logo.complete) {
    logo.addEventListener('load', fadeOutAndRemove, { once: true });
    logo.addEventListener('error', fadeOutAndRemove, { once: true });
  } else {
    fadeOutAndRemove();
  }
});

document.addEventListener('DOMContentLoaded', () => {
  // Cinematic hero (sections/nh-hero.liquid): sound toggle + scroll cue
  document.querySelectorAll('.nh-hero').forEach((hero) => {
    const video = hero.querySelector('[data-nh-hero-video]');
    const soundToggle = hero.querySelector('[data-nh-sound-toggle]');

    if (video && soundToggle) {
      const mutedIcon = soundToggle.querySelector('[data-nh-icon-muted]');
      const unmutedIcon = soundToggle.querySelector('[data-nh-icon-unmuted]');

      soundToggle.addEventListener('click', () => {
        video.muted = !video.muted;
        const isMuted = video.muted;

        soundToggle.setAttribute('aria-pressed', String(!isMuted));
        soundToggle.setAttribute('aria-label', isMuted ? 'Unmute video' : 'Mute video');
        mutedIcon.hidden = !isMuted;
        unmutedIcon.hidden = isMuted;
      });
    }

    const scrollCue = hero.querySelector('[data-nh-scroll-cue]');
    if (scrollCue) {
      scrollCue.addEventListener('click', () => {
        const section = hero.closest('.shopify-section');
        const next = section ? section.nextElementSibling : null;
        if (next) {
          next.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }
  });

  // USP strip (sections/nh-usp-strip.liquid): sync pagination dots with the
  // mobile scroll-snap carousel.
  document.querySelectorAll('.nh-usp').forEach((strip) => {
    const track = strip.querySelector('[data-nh-usp-track]');
    const dots = strip.querySelectorAll('[data-nh-usp-dot]');
    if (!track || !dots.length) return;

    const items = Array.from(track.querySelectorAll('.nh-usp__item'));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = items.indexOf(entry.target);
          dots.forEach((dot, i) => dot.classList.toggle('active', i === index));
        });
      },
      { root: track, threshold: 0.6 }
    );

    items.forEach((item) => observer.observe(item));

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = Number(dot.dataset.index);
        const target = items[index];
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
      });
    });
  });

  // FAQ accordion (sections/nh-faq.liquid): toggle aria-expanded / .is-open,
  // optionally closing other rows when only one may be open at a time.
  document.querySelectorAll('[data-nh-faq]').forEach((faq) => {
    const allowMultiple = faq.dataset.nhFaqMultiple === 'true';
    const items = Array.from(faq.querySelectorAll('.nh-faq__item'));

    items.forEach((item) => {
      const button = item.querySelector('.nh-faq__question');
      if (!button) return;

      button.addEventListener('click', () => {
        const isOpen = button.getAttribute('aria-expanded') === 'true';

        if (!allowMultiple && !isOpen) {
          items.forEach((other) => {
            if (other === item) return;
            const otherButton = other.querySelector('.nh-faq__question');
            if (otherButton) otherButton.setAttribute('aria-expanded', 'false');
            other.classList.remove('is-open');
          });
        }

        button.setAttribute('aria-expanded', String(!isOpen));
        item.classList.toggle('is-open', !isOpen);
      });
    });
  });

  // Reviews (sections/nh-reviews.liquid): sync pagination dots with the
  // scroll-snap review carousel.
  document.querySelectorAll('.nh-reviews').forEach((section) => {
    const track = section.querySelector('[data-nh-reviews-track]');
    const dots = section.querySelectorAll('[data-nh-reviews-dot]');
    if (!track || !dots.length) return;

    const items = Array.from(track.querySelectorAll('.nh-reviews__item'));

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting);
        if (!visible.length) return;
        const mostVisible = visible.reduce((a, b) => (b.intersectionRatio > a.intersectionRatio ? b : a));
        const index = items.indexOf(mostVisible.target);
        dots.forEach((dot, i) => dot.classList.toggle('active', i === index));
      },
      { root: track, threshold: 0.6 }
    );

    items.forEach((item) => observer.observe(item));

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = Number(dot.dataset.index);
        const target = items[index];
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
      });
    });
  });

  // Featured watches (sections/nh-featured-watches.liquid): scroll the
  // product carousel by one card-width per arrow click.
  document.querySelectorAll('.nh-featured__carousel').forEach((carousel) => {
    const track = carousel.querySelector('[data-nh-featured-track]');
    if (!track) return;
    const section = carousel.closest('.nh-featured');
    const prevBtn = section ? section.querySelector('[data-nh-featured-prev]') : null;
    const nextBtn = section ? section.querySelector('[data-nh-featured-next]') : null;

    const scrollByCard = (direction) => {
      const item = track.querySelector('.nh-featured__item');
      if (!item) return;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const delta = (item.getBoundingClientRect().width + gap) * direction;
      track.scrollBy({ left: delta, behavior: 'smooth' });
    };

    if (prevBtn) prevBtn.addEventListener('click', () => scrollByCard(-1));
    if (nextBtn) nextBtn.addEventListener('click', () => scrollByCard(1));
  });

  // Watch builder steps (sections/nh-builder-steps.liquid): tabbed step
  // panels with crossfade transitions, lazy-played videos, and optional
  // auto-advance.
  document.querySelectorAll('[data-nh-builder]').forEach((builder) => {
    const tabs = Array.from(builder.querySelectorAll('[data-nh-builder-tab]'));
    const panels = Array.from(builder.querySelectorAll('.nh-builder__panel'));
    if (!tabs.length || !panels.length) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const autoAdvance = builder.dataset.nhBuilderAutoAdvance === 'true' && !prefersReducedMotion;
    const interval = Number(builder.dataset.nhBuilderInterval) || 6000;

    let isInView = false;
    let autoAdvanceTimer = null;

    const getPanelVideo = (panel) => {
      const video = panel.querySelector('[data-nh-builder-video]');
      if (!video) return null;
      return { video, source: video.querySelector('source[data-src]') };
    };

    const playPanelVideo = (panel) => {
      const ref = getPanelVideo(panel);
      if (!ref) return;
      if (ref.source && !ref.source.src) {
        ref.source.src = ref.source.dataset.src;
        ref.video.load();
      }
      ref.video.play().catch(() => {});
      ref.video.classList.add('is-playing');
    };

    const pausePanelVideo = (panel) => {
      const ref = getPanelVideo(panel);
      if (!ref) return;
      ref.video.pause();
      ref.video.classList.remove('is-playing');
    };

    const panelsWrap = builder.querySelector('.nh-builder__panels');
    let heightResetTimer = null;

    const activate = (index) => {
      // Measure before the swap so differing panel heights animate smoothly
      // instead of snapping (the CSS transition on .nh-builder__panels does
      // the easing; we just pin start/end heights around the class toggle).
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const prevHeight = panelsWrap && !reduceMotion ? panelsWrap.offsetHeight : null;

      tabs.forEach((tab, i) => {
        const isActive = i === index;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', String(isActive));
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      panels.forEach((panel, i) => {
        const isActive = i === index;
        panel.classList.toggle('is-active', isActive);
        panel.setAttribute('aria-hidden', String(!isActive));

        if (isActive) {
          panel.removeAttribute('inert');
          if (isInView) playPanelVideo(panel);
        } else {
          panel.setAttribute('inert', '');
          pausePanelVideo(panel);
        }
      });

      if (panelsWrap && prevHeight !== null) {
        panelsWrap.style.height = '';
        const nextHeight = panelsWrap.offsetHeight;
        if (nextHeight !== prevHeight) {
          clearTimeout(heightResetTimer);
          panelsWrap.style.height = `${prevHeight}px`;
          void panelsWrap.offsetHeight; // reflow so the transition runs
          panelsWrap.style.height = `${nextHeight}px`;
          heightResetTimer = setTimeout(() => {
            panelsWrap.style.height = '';
          }, 500);
        }
      }
    };

    const stopAutoAdvance = () => {
      if (autoAdvanceTimer) {
        clearInterval(autoAdvanceTimer);
        autoAdvanceTimer = null;
      }
    };

    const startAutoAdvance = () => {
      if (!autoAdvance || !isInView || tabs.length < 2 || autoAdvanceTimer) return;
      autoAdvanceTimer = setInterval(() => {
        const current = tabs.findIndex((tab) => tab.classList.contains('is-active'));
        activate((current + 1) % tabs.length);
      }, interval);
    };

    const restartAutoAdvance = () => {
      stopAutoAdvance();
      startAutoAdvance();
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => {
        activate(index);
        restartAutoAdvance();
      });

      tab.addEventListener('keydown', (event) => {
        let newIndex = null;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          newIndex = (index + 1) % tabs.length;
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          newIndex = (index - 1 + tabs.length) % tabs.length;
        } else if (event.key === 'Home') {
          newIndex = 0;
        } else if (event.key === 'End') {
          newIndex = tabs.length - 1;
        }

        if (newIndex !== null) {
          event.preventDefault();
          activate(newIndex);
          tabs[newIndex].focus();
          restartAutoAdvance();
        }
      });
    });

    builder.addEventListener('mouseenter', stopAutoAdvance);
    builder.addEventListener('mouseleave', startAutoAdvance);
    builder.addEventListener('focusin', stopAutoAdvance);
    builder.addEventListener('focusout', startAutoAdvance);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          isInView = entry.isIntersecting;
          const activeIndex = tabs.findIndex((tab) => tab.classList.contains('is-active'));
          const activePanel = panels[activeIndex];
          if (isInView) {
            if (activePanel) playPanelVideo(activePanel);
            startAutoAdvance();
          } else {
            if (activePanel) pausePanelVideo(activePanel);
            stopAutoAdvance();
          }
        });
      },
      { threshold: 0.3 }
    );
    observer.observe(builder);
  });

  // Collections row (sections/nh-collections.liquid): arrow scroll,
  // mouse-drag scrolling, and lazy-load/lazy-play each tile's video.
  document.querySelectorAll('.nh-collections__carousel').forEach((carousel) => {
    const track = carousel.querySelector('[data-nh-collections-track]');
    if (!track) return;
    const section = carousel.closest('.nh-collections');
    const prevBtn = section ? section.querySelector('[data-nh-collections-prev]') : null;
    const nextBtn = section ? section.querySelector('[data-nh-collections-next]') : null;

    const scrollByTile = (direction) => {
      const item = track.querySelector('.nh-collections__item');
      if (!item) return;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const delta = (item.getBoundingClientRect().width + gap) * direction;
      track.scrollBy({ left: delta, behavior: 'smooth' });
    };

    if (prevBtn) prevBtn.addEventListener('click', () => scrollByTile(-1));
    if (nextBtn) nextBtn.addEventListener('click', () => scrollByTile(1));

    // Mouse-drag scrolling (touch/trackpad already scroll natively).
    let isDown = false;
    let dragged = false;
    let startX = 0;
    let scrollStart = 0;

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch') return;
      isDown = true;
      dragged = false;
      startX = event.clientX;
      scrollStart = track.scrollLeft;
      track.classList.add('is-dragging');
    });

    track.addEventListener('pointermove', (event) => {
      if (!isDown) return;
      const delta = event.clientX - startX;
      if (Math.abs(delta) > 5) dragged = true;
      track.scrollLeft = scrollStart - delta;
    });

    const endDrag = () => {
      isDown = false;
      track.classList.remove('is-dragging');
    };

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointerleave', endDrag);

    track.addEventListener(
      'click',
      (event) => {
        if (dragged) {
          event.preventDefault();
          dragged = false;
        }
      },
      true
    );

    // Lazy-load and lazy-play each tile's background video while in view.
    track.querySelectorAll('.nh-collections__tile').forEach((tile) => {
      const video = tile.querySelector('[data-nh-collections-video]');
      if (!video) return;
      const source = video.querySelector('source[data-src]');

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              if (source && !source.src) {
                source.src = source.dataset.src;
                video.load();
              }
              video.play().catch(() => {});
              video.classList.add('is-playing');
            } else {
              video.pause();
              video.classList.remove('is-playing');
            }
          });
        },
        { threshold: 0.4 }
      );

      observer.observe(tile);
    });
  });

  // UGC video gallery (sections/nh-ugc-gallery.liquid): arrow scroll,
  // mouse-drag scrolling, pagination-dot sync, and click-to-play videos
  // (with sound) — only one tile plays at a time.
  document.querySelectorAll('.nh-ugc__carousel').forEach((carousel) => {
    const track = carousel.querySelector('[data-nh-ugc-track]');
    if (!track) return;
    const section = carousel.closest('.nh-ugc');
    const prevBtn = section ? section.querySelector('[data-nh-ugc-prev]') : null;
    const nextBtn = section ? section.querySelector('[data-nh-ugc-next]') : null;
    const dots = section ? section.querySelectorAll('[data-nh-ugc-dot]') : [];
    const items = Array.from(track.querySelectorAll('.nh-ugc__item'));

    const scrollByTile = (direction) => {
      const item = track.querySelector('.nh-ugc__item');
      if (!item) return;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const delta = (item.getBoundingClientRect().width + gap) * direction;
      track.scrollBy({ left: delta, behavior: 'smooth' });
    };

    if (prevBtn) prevBtn.addEventListener('click', () => scrollByTile(-1));
    if (nextBtn) nextBtn.addEventListener('click', () => scrollByTile(1));

    if (dots.length) {
      const observer = new IntersectionObserver(
        (entries) => {
          const visible = entries.filter((entry) => entry.isIntersecting);
          if (!visible.length) return;
          const mostVisible = visible.reduce((a, b) => (b.intersectionRatio > a.intersectionRatio ? b : a));
          const index = items.indexOf(mostVisible.target);
          dots.forEach((dot, i) => dot.classList.toggle('active', i === index));
        },
        { root: track, threshold: 0.6 }
      );

      items.forEach((item) => observer.observe(item));

      dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const index = Number(dot.dataset.index);
          const target = items[index];
          if (target) {
            target.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
          }
        });
      });
    }

    // Mouse-drag scrolling (touch/trackpad already scroll natively).
    let isDown = false;
    let dragged = false;
    let startX = 0;
    let scrollStart = 0;

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch') return;
      isDown = true;
      dragged = false;
      startX = event.clientX;
      scrollStart = track.scrollLeft;
      track.classList.add('is-dragging');
    });

    track.addEventListener('pointermove', (event) => {
      if (!isDown) return;
      const delta = event.clientX - startX;
      if (Math.abs(delta) > 5) dragged = true;
      track.scrollLeft = scrollStart - delta;
    });

    const endDrag = () => {
      isDown = false;
      track.classList.remove('is-dragging');
    };

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointerleave', endDrag);

    track.addEventListener(
      'click',
      (event) => {
        if (dragged) {
          event.preventDefault();
          event.stopPropagation(); // a drag must not open the shared video modal
          dragged = false;
        }
      },
      true
    );

    // Inline muted autoplay + tap-to-open-modal (with sound) are handled by the
    // shared §22 controller. The old inline click-to-play-with-sound was removed
    // so audio now only ever plays inside the modal.
  });

  // Announcement bar + Conf. Urgency countdowns — ONE globally-synced source.
  // Both sections render [data-nh-countdown] and share this logic, so they are
  // always identical to each other AND to every other visitor.
  //
  // Fixed 48-hour cycle anchored to a shared UTC epoch: remaining time is a pure
  // function of absolute UTC time (Date.now(), which is UTC-based), so device
  // time zone never affects the value. It auto-resets to 48:00:00 at zero via the
  // modulo — no per-visitor localStorage, no manual reset logic, no drift.
  //   remaining = CYCLE_MS - ((Date.now() - EPOCH_UTC_MS) mod CYCLE_MS)
  (function () {
    var EPOCH_UTC_MS = Date.UTC(2024, 0, 1, 0, 0, 0); // 2024-01-01T00:00:00Z — fixed global anchor
    var CYCLE_MS = 48 * 60 * 60 * 1000;               // 48-hour cycle

    // Single shared source of truth — both timers call this, so they can't diverge.
    window.nhSyncedCountdownRemaining = function () {
      var elapsed = Date.now() - EPOCH_UTC_MS;
      return CYCLE_MS - (((elapsed % CYCLE_MS) + CYCLE_MS) % CYCLE_MS);
    };

    var pad = function (n) { return String(n).padStart(2, '0'); };

    function bind(timer) {
      if (timer.dataset.nhCountdownBound === '1') return; // guard against duplicate intervals
      var hoursEl = timer.querySelector('[data-nh-countdown-hours]');
      var minutesEl = timer.querySelector('[data-nh-countdown-minutes]');
      var secondsEl = timer.querySelector('[data-nh-countdown-seconds]');
      if (!hoursEl || !minutesEl || !secondsEl) return;
      timer.dataset.nhCountdownBound = '1';

      var intervalId;
      var update = function () {
        var totalSeconds = Math.floor(window.nhSyncedCountdownRemaining() / 1000);
        hoursEl.textContent = pad(Math.floor(totalSeconds / 3600));
        minutesEl.textContent = pad(Math.floor((totalSeconds % 3600) / 60));
        secondsEl.textContent = pad(totalSeconds % 60);
      };
      update(); // paint immediately, no 1s flash of the server-rendered placeholder
      intervalId = setInterval(update, 1000);
      timer.addEventListener('shopify:section:unload', function () {
        clearInterval(intervalId);
        delete timer.dataset.nhCountdownBound;
      });
    }

    function scan(root) {
      (root || document).querySelectorAll('[data-nh-countdown]').forEach(bind);
    }
    scan();
    // Theme-editor re-render: bind any newly-inserted timer (the guard prevents dupes).
    document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
  })();

  // Stats band (sections/nh-stats.liquid): animate each number counting up
  // from 0 to its target the first time the section scrolls into view.
  // The server-rendered markup already shows the final formatted value, so
  // reduced-motion users (and anyone before JS runs) simply see the correct
  // number with no animation.
  if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    document.querySelectorAll('[data-nh-stats]').forEach((section) => {
      const values = section.querySelectorAll('[data-nh-stats-value]');
      if (!values.length) return;

      const duration = (Number(section.dataset.nhStatsDuration) || 1.8) * 1000;

      const animateValue = (el) => {
        const target = Number(el.dataset.target) || 0;
        const decimals = Number(el.dataset.decimals) || 0;
        const start = performance.now();

        const step = (now) => {
          const progress = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          el.textContent = (target * eased).toFixed(decimals);

          if (progress < 1) {
            requestAnimationFrame(step);
          } else {
            el.textContent = target.toFixed(decimals);
          }
        };

        el.textContent = (0).toFixed(decimals);
        requestAnimationFrame(step);
      };

      const observer = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            values.forEach(animateValue);
            obs.disconnect();
          });
        },
        { threshold: 0.4 }
      );

      observer.observe(section);
    });
  }

  // Process steps (sections/nh-process.liquid): lazy-load and lazy-play each
  // card's background video only while it's in view.
  document.querySelectorAll('.nh-process__card').forEach((card) => {
    const video = card.querySelector('[data-nh-process-video]');
    if (!video) return;
    const source = video.querySelector('source[data-src]');

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (source && !source.src) {
              source.src = source.dataset.src;
              video.load();
            }
            video.play().catch(() => {});
            video.classList.add('is-playing');
          } else {
            video.pause();
            video.classList.remove('is-playing');
          }
        });
      },
      { threshold: 0.4 }
    );

    observer.observe(card);
  });

  // Configurator style showcases (sections/nh-conf-styles.liquid): lazy-load
  // and lazy-play each panel's background video while it's in view.
  document.querySelectorAll('[data-nh-cs-video]').forEach((video) => {
    const source = video.querySelector('source[data-src]');
    if (!source) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!source.src) { source.src = source.dataset.src; video.load(); }
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.3 }
    );
    observer.observe(video);
  });

  // ── NH Men's Grid (sections/nh-mens-grid.liquid) ──────────────────────────
  // Client-side tag filter, sort, and Quick View drawer.
  // Multiple instances are supported (each .nh-mg is a self-contained scope).
  document.querySelectorAll('[data-nh-mg]').forEach((section) => {
    const gridEl = section.querySelector('[data-nh-mg-grid]');
    const allCards = gridEl ? Array.from(gridEl.querySelectorAll('[data-nh-mg-card]')) : [];
    const currency = section.dataset.currency || 'CAD';
    const showFilters = section.dataset.showFilters === 'true';
    const showSort = section.dataset.showSort === 'true';
    const showQv = section.dataset.showQv === 'true';

    // ── Sort ──────────────────────────────────────────────────────────────
    if (showSort) {
      const sortSelect = section.querySelector('[data-nh-sort]');
      if (sortSelect && gridEl) {
        sortSelect.addEventListener('change', () => {
          const val = sortSelect.value;
          const sorted = [...allCards].sort((a, b) => {
            if (val === 'price-asc') return Number(a.dataset.price) - Number(b.dataset.price);
            if (val === 'price-desc') return Number(b.dataset.price) - Number(a.dataset.price);
            if (val === 'title-asc') return a.dataset.title.localeCompare(b.dataset.title);
            if (val === 'title-desc') return b.dataset.title.localeCompare(a.dataset.title);
            return Number(a.dataset.order) - Number(b.dataset.order); // 'default': restore original
          });
          sorted.forEach((card) => gridEl.appendChild(card));
        });
      }
    }

    // ── Filters ───────────────────────────────────────────────────────────
    if (showFilters) {
      const filterWrap = section.querySelector('[data-nh-mg-filters]');
      if (filterWrap && allCards.length > 0) {
        // Build unique sorted tag set from card data
        const tagSet = new Set();
        allCards.forEach((card) => {
          (card.dataset.tags || '').split(' ').forEach((t) => { if (t) tagSet.add(t); });
        });

        if (tagSet.size > 0) {
          let activeFilter = 'all';

          const makeButton = (filterVal, label, cls) => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = cls;
            btn.dataset.nhMgFilter = filterVal;
            btn.textContent = label;
            return btn;
          };

          // "All" pill
          const allPill = makeButton('all', 'All', 'nh-mg-filter-pill is-active');
          filterWrap.appendChild(allPill);

          // Tag pills — humanise handleized tag back to Title Case
          const humanise = (handle) =>
            handle.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

          [...tagSet].sort().forEach((tag) => {
            filterWrap.appendChild(makeButton(tag, humanise(tag), 'nh-mg-filter-pill'));
          });

          // "Clear filters" pill (hidden until a filter is active)
          const clearBtn = document.createElement('button');
          clearBtn.type = 'button';
          clearBtn.className = 'nh-mg-filter-clear';
          clearBtn.textContent = 'Clear filters';
          clearBtn.style.display = 'none';
          filterWrap.appendChild(clearBtn);

          const applyFilter = () => {
            allCards.forEach((card) => {
              const tags = (card.dataset.tags || '').split(' ');
              card.style.display = (activeFilter === 'all' || tags.includes(activeFilter)) ? '' : 'none';
            });
          };

          filterWrap.addEventListener('click', (e) => {
            const pill = e.target.closest('[data-nh-mg-filter]');
            if (!pill) return;
            activeFilter = pill.dataset.nhMgFilter;
            filterWrap.querySelectorAll('[data-nh-mg-filter]').forEach((p) => {
              p.classList.toggle('is-active', p.dataset.nhMgFilter === activeFilter);
            });
            clearBtn.style.display = activeFilter === 'all' ? 'none' : '';
            applyFilter();
          });

          clearBtn.addEventListener('click', () => {
            activeFilter = 'all';
            filterWrap.querySelectorAll('[data-nh-mg-filter]').forEach((p) => {
              p.classList.toggle('is-active', p.dataset.nhMgFilter === 'all');
            });
            clearBtn.style.display = 'none';
            applyFilter();
          });
        }
      }
    }

    // ── Quick View ────────────────────────────────────────────────────────
    if (!showQv) return;

    const drawer = section.querySelector('[data-nh-qv-drawer]');
    const drawerBody = drawer ? drawer.querySelector('[data-nh-qv-body]') : null;
    if (!drawer || !drawerBody) return;

    let lastFocus = null;

    const openDrawer = () => {
      lastFocus = document.activeElement;
      drawer.removeAttribute('hidden');
      requestAnimationFrame(() => {
        drawer.classList.add('is-open');
        const firstFocusable = drawer.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (firstFocusable) firstFocusable.focus();
      });
      document.body.style.overflow = 'hidden';
    };

    const closeDrawer = () => {
      drawer.classList.remove('is-open');
      document.body.style.overflow = '';
      // Wait for transition before hiding
      drawer.addEventListener(
        'transitionend',
        () => {
          drawer.setAttribute('hidden', '');
          if (lastFocus) lastFocus.focus();
        },
        { once: true }
      );
    };

    // Close on backdrop click or close button
    drawer.querySelectorAll('[data-nh-qv-close]').forEach((el) => {
      el.addEventListener('click', closeDrawer);
    });

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !drawer.hasAttribute('hidden')) closeDrawer();
    });

    // Money formatter — uses Intl.NumberFormat with store currency
    const fmt = new Intl.NumberFormat('en', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
    });
    const formatMoney = (cents) => fmt.format(cents / 100);

    // Strip HTML for description preview
    const stripHTML = (html) => {
      try {
        return new DOMParser().parseFromString(html || '', 'text/html').body.textContent || '';
      } catch (_) {
        return '';
      }
    };

    // Listen for Quick View trigger buttons within this section
    section.querySelectorAll('[data-nh-mg-qv]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const handle = btn.dataset.nhMgQv;

        // Show loading state + open drawer
        drawerBody.innerHTML = `
          <div class="nh-qv-loading" aria-live="polite" aria-label="Loading product">
            <div class="nh-qv-loading__dot"></div>
            <div class="nh-qv-loading__dot"></div>
            <div class="nh-qv-loading__dot"></div>
          </div>`;
        openDrawer();

        try {
          const res = await fetch(`/products/${handle}.js`);
          if (!res.ok) throw new Error('fetch failed');
          const p = await res.json();

          const img = p.images && p.images.length > 0
            ? `<img class="nh-qv-img" src="${p.images[0]}" alt="${p.title.replace(/"/g, '&quot;')}" loading="lazy">`
            : '';

          const onSale = p.compare_at_price_max > p.price_min;
          const compareStr = onSale
            ? `<s class="nh-qv-compare">${formatMoney(p.compare_at_price_max)}</s>`
            : '';

          const desc = stripHTML(p.body_html);
          const descHtml = desc
            ? `<p class="nh-qv-desc">${desc.length > 200 ? desc.slice(0, 200) + '…' : desc}</p>`
            : '';

          // Variant selector — only when >1 available variant
          const availableVariants = p.variants.filter((v) => v.available);
          let variantHtml = '';
          if (p.variants.length > 1) {
            const options = p.variants
              .map(
                (v) =>
                  `<option value="${v.id}" data-price="${v.price}" ${!v.available ? 'disabled' : ''}>${v.title}${!v.available ? ' – Sold out' : ''}</option>`
              )
              .join('');
            variantHtml = `
              <label class="nh-qv-label" for="nh-qv-variant-${handle}">Option</label>
              <select class="nh-qv-variant-select" id="nh-qv-variant-${handle}" data-nh-qv-variant>
                ${options}
              </select>`;
          }

          drawerBody.innerHTML = `
            <div class="nh-qv-product">
              ${img}
              <div class="nh-qv-info">
                <p class="nh-qv-vendor">${p.vendor || ''}</p>
                <h2 class="nh-qv-title">${p.title}</h2>
                <p class="nh-qv-price">
                  <span class="nh-qv-price-current">${formatMoney(p.price_min)}</span>
                  ${compareStr}
                </p>
                ${variantHtml}
                ${descHtml}
                <a href="/products/${p.handle}" class="nh-qv-cta">View Full Details</a>
              </div>
            </div>`;

          // Update price when variant changes
          const variantSel = drawerBody.querySelector('[data-nh-qv-variant]');
          if (variantSel) {
            variantSel.addEventListener('change', () => {
              const opt = variantSel.selectedOptions[0];
              const priceEl = drawerBody.querySelector('.nh-qv-price-current');
              if (priceEl && opt) priceEl.textContent = formatMoney(Number(opt.dataset.price));
            });
          }
        } catch (_) {
          drawerBody.innerHTML = `
            <div class="nh-qv-error">
              <p>Unable to load product details.</p>
              <a href="/products/${handle}" class="nh-qv-cta">View on product page →</a>
            </div>`;
        }
      });
    });
  });

  // Product reviews section (sections/nh-product-reviews.liquid):
  // adds .is-visible which triggers both the star fill animation (CSS) and
  // the card stagger entrance (CSS transition-delay per card).
  document.querySelectorAll('[data-nh-rv]').forEach((el) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      el.classList.add('is-visible');
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.classList.add('is-visible');
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
  });

  // Product story scroll-reveal (sections/nh-product-story.liquid).
  // Adds .is-visible to the section root when 15% of it enters the viewport;
  // CSS transitions on .nh-ps__media and .nh-ps__content do the rest.
  document.querySelectorAll('[data-nh-ps]').forEach((el) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      el.classList.add('is-visible');
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.classList.add('is-visible');
            observer.disconnect();
          }
        });
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
  });

  // Product specs staggered reveal (sections/nh-product-specs.liquid).
  // The count-up is handled by the existing [data-nh-stats] observer above.
  // This adds .nh-psp--visible for the CSS stagger-fade on the item cards.
  document.querySelectorAll('.nh-psp').forEach((el) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      el.classList.add('nh-psp--visible');
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.classList.add('nh-psp--visible');
            observer.disconnect();
          }
        });
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
  });
});

// =============================================================================
// Section 13 — Mens mega menu: CSS-var height patch
// =============================================================================
// Horizon's header-menu.js reads submenu.offsetHeight synchronously
// immediately after setting [data-active]. When content-visibility:auto is in
// play (or the CSS cascade hasn't flushed yet), offsetHeight returns 0, so
// --full-open-header-height stays at 0 and the clip-path collapses everything.
//
// This patch listens for the SAME pointerenter signal (same capture pattern as
// Horizon's Component system) and, after a double-rAF (guaranteeing that the
// CSS content-visibility:visible has been applied and the browser has had two
// render frames to lay out the content), re-measures offsetHeight and writes
// --submenu-height and --full-open-header-height on header-component.
// =============================================================================
(function () {
  document.addEventListener('pointerenter', function (event) {
    // Mirror Horizon's getElement() check: only act when the EXACT element
    // entered has on:pointerenter — that element is the <li>.
    var path = event.composedPath ? event.composedPath() : [];
    var target = path[0] || event.target;
    if (!target || !target.hasAttribute || !target.hasAttribute('on:pointerenter')) return;

    // Only patch our custom mega menu.
    var submenu = target.querySelector && target.querySelector('.nh-mm-submenu');
    if (!submenu) return;

    // Double rAF: let Horizon's activate() run (sync), then let the browser
    // paint two frames so CSS content-visibility:visible is fully applied.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        var headerComp = submenu.closest && submenu.closest('header-component');
        if (!headerComp) return;

        // Bail if the user already moved away before the frames fired.
        var li = submenu.parentElement;
        var link = li && li.querySelector('[ref="menuitem"]');
        if (!link || link.getAttribute('aria-expanded') !== 'true') return;

        var h = submenu.offsetHeight;
        if (!h) return;

        // headerComp.offsetHeight is the closed header row height (the
        // submenu is position:absolute so it doesn't inflate this value).
        var headerH = headerComp.offsetHeight;
        headerComp.style.setProperty('--submenu-height', h + 'px');
        headerComp.style.setProperty('--full-open-header-height', (h + headerH) + 'px');
      });
    });
  }, { capture: true });
})();

// =============================================================================
// Section 15 — Grid quick add-to-cart (gold circle next to "View Watch")
// =============================================================================
// Delegated handler for [data-nh-mg-atc] buttons rendered by nh-mens-grid.
// Adds variant qty 1 via /cart/add.js, then notifies Horizon's cart icon with
// a bubbling cart:update event (cart-icon.js reads detail.data.itemCount).
// =============================================================================
(function () {
  document.addEventListener('click', function (event) {
    var btn = event.target.closest && event.target.closest('[data-nh-mg-atc]');
    if (!btn) return;
    event.preventDefault();
    event.stopPropagation();
    if (btn.dataset.nhAtcState === 'loading') return;

    var variantId = parseInt(btn.dataset.nhMgAtc, 10);
    if (!variantId) return;
    btn.dataset.nhAtcState = 'loading';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
      .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
      .then(function () { return fetch('/cart.js').then(function (r) { return r.json(); }); })
      .then(function (cart) {
        btn.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: { resource: cart, sourceId: 'nh-quick-atc', data: { source: 'nh-quick-atc', itemCount: cart.item_count, variantId: String(variantId) } }
        }));
        btn.dataset.nhAtcState = 'added';
        setTimeout(function () { delete btn.dataset.nhAtcState; }, 1600);
      })
      .catch(function () {
        delete btn.dataset.nhAtcState;
        btn.animate(
          [{ transform: 'translateX(0)' }, { transform: 'translateX(-3px)' }, { transform: 'translateX(3px)' }, { transform: 'translateX(0)' }],
          { duration: 240 }
        );
      });
  });
})();

// =============================================================================
// Section 18 — Shared smooth-scroll CTAs (data-nh-conf-scroll)
// =============================================================================
// Any anchor with data-nh-conf-scroll="<selector>" smooth-scrolls to the first
// matching element instead of following its href. If no match exists on the
// current page, the href navigation proceeds as a fallback (e.g. the urgency
// CTA still goes to /pages/configurator from other pages). Sticky-header
// offset + prefers-reduced-motion respected.
// =============================================================================
(function () {
  document.addEventListener('click', function (event) {
    var cta = event.target.closest && event.target.closest('[data-nh-conf-scroll]');
    if (!cta) return;
    var target = document.querySelector(cta.getAttribute('data-nh-conf-scroll'));
    if (!target) return; // fall through to href
    event.preventDefault();
    var header = document.getElementById('header-component');
    var offset = header ? header.offsetHeight : 0;
    var top = target.getBoundingClientRect().top + window.scrollY - offset;
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: top, behavior: reduce ? 'auto' : 'smooth' });
  });
})();

// =============================================================================
// Section 19 — Fix 3: Configurator-only "Watch Builder Tool" scroll target
// =============================================================================
// sections/nh-builder-steps.liquid's "WATCH BUILDER TOOL" button is reused
// across multiple pages (homepage, 404-template homepage clone, and the
// configurator page). On the homepage it correctly links to
// /pages/configurator. On the configurator page itself, that same href just
// reloads the page it's already on — so ONLY there, redirect the click to a
// smooth-scroll up to the NH Conf. Grid Navigation section instead. Every
// other page's button keeps its existing href/behavior completely untouched
// (this whole block no-ops immediately if the pathname doesn't match).
// =============================================================================
(function () {
  if (window.location.pathname !== '/pages/configurator') return;

  // NH Conf. Grid Navigation section (sections/nh-conf-grid-nav.liquid) —
  // found via its own scoped wrapper class, not a hardcoded pixel offset.
  var target = document.querySelector('[class*="cfgr-wrapper-"]');
  if (!target) return;

  document.querySelectorAll('.nh-builder__button').forEach(function (btn) {
    btn.addEventListener('click', function (event) {
      event.preventDefault();
      var header = document.getElementById('header-component');
      var offset = header ? header.offsetHeight : 0;
      var top = target.getBoundingClientRect().top + window.scrollY - offset;
      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: top, behavior: reduce ? 'auto' : 'smooth' });
    });
  });
})();

// =============================================================================
// Section 20 — Fix 4: Mobile product grid view toggle (1-col / 2-col)
// =============================================================================
// Behavior for the toggle markup in sections/nh-mens-grid.liquid — shared
// across every page that renders that section (Mens, Womens, and all 9 model
// sub-collection pages), so this one handler covers all of them. Persists
// the choice for the session (sessionStorage) so it holds while browsing;
// defaults to single-column ('1') on a fresh session. Styling (including
// hiding the toggle on desktop) lives entirely in custom.css §22 — this
// script only toggles the `nh-mg__grid--cols-2` class and button states.
// =============================================================================
(function () {
  var STORAGE_KEY = 'nh-mg-view';

  document.querySelectorAll('[data-nh-mg-view-toggle]').forEach(function (toggle) {
    var grid = toggle.parentElement ? toggle.parentElement.querySelector('[data-nh-mg-grid]') : null;
    if (!grid) return;
    var buttons = toggle.querySelectorAll('[data-nh-mg-view]');

    var apply = function (view) {
      grid.classList.toggle('nh-mg__grid--cols-2', view === '2');
      buttons.forEach(function (btn) {
        var active = btn.dataset.nhMgView === view;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', String(active));
      });
    };

    apply(sessionStorage.getItem(STORAGE_KEY) || '1');

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var view = btn.dataset.nhMgView;
        sessionStorage.setItem(STORAGE_KEY, view);
        apply(view);
      });
    });
  });
})();

// =============================================================================
// Section 22 — Fix 1: Hero/banner video autoplay + prefers-reduced-motion
// =============================================================================
// Scope: hero/banner BACKGROUND videos only — the homepage hero
// (.nh-hero__media-el), page heroes (.nh-mens-hero__bg — Mens + Womens),
// the configurator hero (.nh-ch__bg), and Horizon's generic
// <video-background-component> used by banner blocks. Deliberately excludes the
// UGC gallery and product video reviews, which are click-to-play by design.
//
// The markup already declares autoplay/muted/loop/playsinline, and the native
// play button Safari/iOS paints when it refuses autoplay is hidden in
// custom.css. This script covers the two remaining requirements:
//   1. prefers-reduced-motion → do NOT autoplay; freeze on the first frame.
//   2. Guarantee playback for lazy/late sources (video-background-component
//      swaps data-video-source → src after load) and retry if a play() promise
//      is rejected, re-attempting on the next loadeddata.
// =============================================================================
(function () {
  var SELECTOR = [
    '.nh-hero__media-el',
    '.nh-mens-hero__bg',
    '.nh-ch__bg',
    'video-background-component video'
  ].join(', ');

  var reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  function applyTo(video) {
    // Enforce the attributes autoplay depends on (muted + playsinline are
    // mandatory on iOS/Safari) and make sure no controls can appear.
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.removeAttribute('controls');
    video.controls = false;

    if (reduceQuery.matches) {
      // Reduced motion: freeze on the first frame rather than autoplaying.
      video.autoplay = false;
      video.removeAttribute('autoplay');
      video.pause();
      try { video.currentTime = 0; } catch (e) { /* not seekable yet */ }
      return;
    }

    video.autoplay = true;
    // If no data has started loading yet (e.g. a deferred source just attached,
    // or preload="metadata" on mobile), kick a load() so play() has something to
    // start — muted is already set above, which is what iOS checks. If play() is
    // still rejected (iOS Low Power Mode blocks all autoplay), the video simply
    // stays paused showing its poster frame — the poster attribute added in the
    // hero sections + the hidden native controls give a clean fallback, no
    // broken state and no play button.
    if (video.readyState === 0) {
      try { video.load(); } catch (e) { /* ignore */ }
    }
    var attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') {
      // Autoplay was blocked — try again once the next frame of data lands
      // rather than leaving a stalled first frame.
      attempt.catch(function () {
        video.addEventListener('loadeddata', function () {
          video.muted = true; // re-assert right before the retry play (iOS)
          var retry = video.play();
          if (retry && typeof retry.catch === 'function') retry.catch(function () {});
        }, { once: true });
      });
    }
  }

  function run() {
    document.querySelectorAll(SELECTOR).forEach(function (video) {
      applyTo(video);
      if (!video.dataset.nhVideoBound) {
        video.dataset.nhVideoBound = '1';
        // Lazy/late sources: re-assert once real data is available.
        video.addEventListener('loadeddata', function () { applyTo(video); });
      }
    });
  }

  run();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  window.addEventListener('load', run);
  // Honour a live change to the OS reduced-motion setting.
  if (reduceQuery.addEventListener) reduceQuery.addEventListener('change', run);
})();

// =============================================================================
// Section 24 — Fix 2: NH Conf grid-nav — one label at a time on mobile
// =============================================================================
// Desktop shows a card's name overlay on :hover, one at a time. Touch has no
// hover, so on mobile we make the overlay follow the card whose centre is
// closest to the viewport centre as the horizontal grid is scrolled. This only
// toggles a `.cfgr-active` class; the visual reveal (opacity + translateY) and
// its transition are the SAME ones the desktop hover uses (custom.css FIX 2).
//
// Distance-from-centre computed inside requestAnimationFrame (never a raw
// scroll handler). Mobile-only: the matchMedia guard means on desktop the
// class is cleared and hover is left completely alone. First visible card is
// active on load. prefers-reduced-motion is handled in CSS (no fade).
// The section's class names carry a dynamic id suffix, so tracks are found by
// their stable id prefix `cfgr-track-` and cards are the <a> children.
// =============================================================================
(function () {
  var mobile = window.matchMedia('(max-width: 768px)');

  function initTrack(track) {
    if (track.dataset.nhCfgrLabelBound) return;
    track.dataset.nhCfgrLabelBound = '1';

    var cards = Array.prototype.filter.call(track.children, function (el) {
      return el.tagName === 'A';
    });
    if (!cards.length) return;

    var scheduled = false;

    function update() {
      scheduled = false;
      if (!mobile.matches) {
        // Desktop: hover owns it — clear any leftover mobile active state.
        cards.forEach(function (c) { c.classList.remove('cfgr-active'); });
        return;
      }
      var viewportCentre = window.innerWidth / 2;
      var best = null;
      var bestDist = Infinity;
      cards.forEach(function (card) {
        var r = card.getBoundingClientRect();
        var dist = Math.abs(r.left + r.width / 2 - viewportCentre);
        if (dist < bestDist) { bestDist = dist; best = card; }
      });
      cards.forEach(function (card) {
        card.classList.toggle('cfgr-active', card === best);
      });
    }

    function schedule() {
      if (!scheduled) { scheduled = true; requestAnimationFrame(update); }
    }

    track.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    if (mobile.addEventListener) mobile.addEventListener('change', schedule);
    schedule(); // initial → first visible card active
  }

  function boot() {
    document.querySelectorAll('[id^="cfgr-track-"]').forEach(initTrack);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // The grid-nav section renders its own inline script after this file may run;
  // re-scan on load in case the track wasn't in the DOM yet.
  window.addEventListener('load', boot);
})();

// =============================================================================
// Section 20 — NH Featured Watches — stacked "deck" carousel
// (sections/nh-featured-watches.liquid)
// =============================================================================
// Layered depth carousel: one focus card in front, adjacent cards peeking
// behind on both sides, further cards fading into depth. Manages an active
// index and toggles is-focus / is-prev(-2) / is-next(-2) / is-hidden classes;
// all motion is CSS transform+opacity (GPU-friendly). Cycled by arrows, dots,
// drag/swipe (pointer events), clicking a side card, and Left/Right keys.
// Add-to-cart on the focus card is handled by the shared §15 delegated handler
// (gold circle uses data-nh-mg-atc) — no ATC code duplicated here.
// Reduced-motion: CSS collapses to a single static focus card; cycling still works.
// =============================================================================
(function () {
  document.querySelectorAll('.nh-fw__carousel').forEach(function (carousel) {
    var stack = carousel.querySelector('[data-nh-fw-track]');
    if (!stack) return;
    var section = carousel.closest('.nh-fw');
    var prevBtn = section ? section.querySelector('[data-nh-fw-prev]') : null;
    var nextBtn = section ? section.querySelector('[data-nh-fw-next]') : null;
    var dotsWrap = section ? section.querySelector('[data-nh-fw-dots]') : null;
    var items = Array.prototype.slice.call(stack.querySelectorAll('.nh-fw__item'));
    if (!items.length) return;

    var loop = stack.getAttribute('data-nh-fw-loop') === 'true';
    var n = items.length;
    var active = 0;
    var STATES = ['is-focus', 'is-next', 'is-prev', 'is-next-2', 'is-prev-2', 'is-hidden'];

    // Pagination dots — one per card.
    var dots = [];
    if (dotsWrap && n > 1) {
      dots = items.map(function (_, i) {
        var d = document.createElement('button');
        d.type = 'button';
        d.className = 'nh-fw__dot';
        d.setAttribute('aria-label', 'Show watch ' + (i + 1));
        d.addEventListener('click', function () { setActive(i); });
        dotsWrap.appendChild(d);
        return d;
      });
    }

    function classFor(offset) {
      if (offset === 0) return 'is-focus';
      if (offset === 1) return 'is-next';
      if (offset === -1) return 'is-prev';
      if (offset === 2) return 'is-next-2';
      if (offset === -2) return 'is-prev-2';
      return 'is-hidden';
    }

    // Cards are absolutely positioned, so give the stack the focus card's height.
    function setHeight() {
      var focus = items[active] || items[0];
      if (focus) stack.style.height = focus.offsetHeight + 'px';
    }

    function render() {
      items.forEach(function (item, i) {
        var offset = i - active;
        if (loop) {
          if (offset > n / 2) offset -= n;
          else if (offset < -n / 2) offset += n;
        }
        var cls = classFor(offset);
        STATES.forEach(function (s) { item.classList.toggle(s, s === cls); });
        item.setAttribute('aria-hidden', cls === 'is-focus' ? 'false' : 'true');
      });
      dots.forEach(function (d, i) {
        d.classList.toggle('is-active', i === active);
        d.setAttribute('aria-current', i === active ? 'true' : 'false');
      });
      if (!loop) {
        if (prevBtn) prevBtn.disabled = active === 0;
        if (nextBtn) nextBtn.disabled = active === n - 1;
      }
      setHeight();
    }

    function setActive(i) {
      active = loop ? (((i % n) + n) % n) : Math.max(0, Math.min(n - 1, i));
      render();
    }
    function next() { setActive(active + 1); }
    function prev() { setActive(active - 1); }

    if (prevBtn) prevBtn.addEventListener('click', prev);
    if (nextBtn) nextBtn.addEventListener('click', next);

    // Left/Right keys cycle when the stack is focused.
    stack.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
    });

    // Drag / swipe (pointer covers mouse + touch). A real drag cycles the deck.
    var down = false, startX = 0, moved = false;
    stack.addEventListener('pointerdown', function (e) {
      down = true; moved = false; startX = e.clientX; stack.classList.add('is-dragging');
    });
    stack.addEventListener('pointermove', function (e) {
      if (down && Math.abs(e.clientX - startX) > 8) moved = true;
    });
    function endPointer(e) {
      if (!down) return;
      down = false;
      stack.classList.remove('is-dragging');
      var dx = (typeof e.clientX === 'number' ? e.clientX : startX) - startX;
      if (Math.abs(dx) > 40) { if (dx < 0) { next(); } else { prev(); } }
    }
    stack.addEventListener('pointerup', endPointer);
    stack.addEventListener('pointercancel', function () { down = false; stack.classList.remove('is-dragging'); });

    // One capture-phase click handler:
    //  • after a real drag → swallow the click;
    //  • on a side card → bring it to focus (no navigate / no add-to-cart);
    //  • on the focus card → let the click through (link + §15 add-to-cart work).
    stack.addEventListener('click', function (e) {
      if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; return; }
      var item = e.target.closest('.nh-fw__item');
      if (!item) return;
      var idx = items.indexOf(item);
      if (idx === active) return;
      e.preventDefault();
      e.stopPropagation();
      setActive(idx);
    }, true);

    // Init + keep the stack height correct as fonts/images settle and on resize.
    render();
    window.addEventListener('load', setHeight);
    var rt;
    window.addEventListener('resize', function () { clearTimeout(rt); rt = setTimeout(setHeight, 120); });
    items.forEach(function (it) {
      var img = it.querySelector('img');
      if (img && !img.complete) img.addEventListener('load', setHeight, { once: true });
    });
  });
})();

// =============================================================================
// Section 21 — NH Packaging (sections/nh-packaging.liquid)
// =============================================================================
// Scroll-reveal (fade + slight rise, image first then items staggered ~80ms via
// IntersectionObserver) + thumbnail-swap for the primary image. Reduced-motion
// honored (instant show, no transform). Scoped per .nh-packaging root; no CLS
// (transform/opacity only, layout space reserved). Vanilla JS.
// =============================================================================
(function () {
  document.querySelectorAll('[data-nh-packaging]').forEach(function (root) {
    // ── Reveal ──
    var reveals = Array.prototype.slice.call(root.querySelectorAll('[data-nh-pkg-reveal]'));
    if (reveals.length) {
      root.classList.add('nh-pkg-ready'); // enables the hidden start-state (JS-only)
      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduce || !('IntersectionObserver' in window)) {
        reveals.forEach(function (el) { el.classList.add('is-revealed'); });
      } else {
        var io = new IntersectionObserver(function (entries, obs) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            reveals.forEach(function (el, i) {
              setTimeout(function () { el.classList.add('is-revealed'); }, i * 80);
            });
            obs.disconnect();
          });
        }, { threshold: 0.15 });
        io.observe(root);
      }
    }

    // ── Thumbnail swap ──
    var mainImg = root.querySelector('[data-nh-pkg-main] .nh-packaging__img');
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-nh-pkg-thumb]'));
    thumbs.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var full = btn.getAttribute('data-full');
        if (mainImg && full) {
          mainImg.removeAttribute('srcset'); // let src win over the responsive set
          mainImg.src = full;
        }
        thumbs.forEach(function (b) { b.classList.remove('is-active'); });
        btn.classList.add('is-active');
      });
    });
  });
})();

// =============================================================================
// Section 22 — NH inline muted-autoplay video + shared click-to-open modal
// =============================================================================
// Used by BOTH nh-product-video-reviews and nh-ugc-gallery. Inline tiles
// ([data-nh-inline-video]) autoplay MUTED + LOOPED, no controls, only while
// visible (IntersectionObserver), reduced-motion/low-power → poster shows.
// Tapping a tile ([data-nh-video-open] with data-nh-video-src) opens ONE shared
// modal that plays the SAME video WITH sound + native controls, from 0. play()
// runs synchronously inside the tap gesture so iOS permits audio. Esc/backdrop/X
// close, focus trapped + returned, body scroll locked, inline loop resumes.
// =============================================================================
(function () {
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ── Shared modal (built once) ──
  var modal, modalVideo, lastOpener = null, resumeVideo = null;

  function buildModal() {
    if (modal) return;
    modal = document.createElement('div');
    modal.className = 'nh-vmodal';
    modal.hidden = true;
    modal.innerHTML =
      '<div class="nh-vmodal__backdrop" data-nh-vmodal-close></div>' +
      '<div class="nh-vmodal__dialog" role="dialog" aria-modal="true" aria-label="Video player">' +
        '<button type="button" class="nh-vmodal__close" data-nh-vmodal-close aria-label="Close video">' +
          '<svg viewBox="0 0 18 18" fill="none" aria-hidden="true"><path d="M1 1l16 16M17 1L1 17" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>' +
        '</button>' +
        '<video class="nh-vmodal__video" playsinline webkit-playsinline controls preload="metadata"></video>' +
      '</div>';
    document.body.appendChild(modal);
    modalVideo = modal.querySelector('.nh-vmodal__video');
    modal.addEventListener('click', function (e) {
      if (e.target.closest('[data-nh-vmodal-close]')) closeModal();
    });
    document.addEventListener('keydown', function (e) {
      if (modal.hidden) return;
      if (e.key === 'Escape') { e.preventDefault(); closeModal(); }
      else if (e.key === 'Tab') trapFocus(e);
    });
  }

  function trapFocus(e) {
    var f = modal.querySelectorAll('button, video, [tabindex]:not([tabindex="-1"])');
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function openModal(src, poster, opener, inlineVideo) {
    if (!src) return;
    buildModal();
    lastOpener = opener || null;
    resumeVideo = inlineVideo || null;
    if (resumeVideo) { try { resumeVideo.pause(); } catch (_) {} }
    if (poster) { modalVideo.poster = poster; } else { modalVideo.removeAttribute('poster'); }
    modalVideo.src = src;
    modalVideo.muted = false;                 // sound ON in the modal
    try { modalVideo.currentTime = 0; } catch (_) {}
    modal.hidden = false;
    void modal.offsetWidth;                   // reflow so the fade transition runs
    modal.classList.add('is-open');
    document.documentElement.classList.add('nh-vmodal-lock');
    modalVideo.play().catch(function () {});  // synchronous in the gesture → iOS allows audio
    var closeBtn = modal.querySelector('.nh-vmodal__close');
    if (closeBtn) closeBtn.focus();
  }

  function closeModal() {
    if (!modal || modal.hidden) return;
    try { modalVideo.pause(); } catch (_) {}
    modalVideo.removeAttribute('src');
    modalVideo.load();                        // fully stop audio + buffering
    modal.classList.remove('is-open');
    modal.hidden = true;
    document.documentElement.classList.remove('nh-vmodal-lock');
    if (resumeVideo) { resumeVideo.play().catch(function () {}); resumeVideo = null; }
    if (lastOpener && lastOpener.focus) lastOpener.focus();
    lastOpener = null;
  }

  // ── Openers: the tile carries data-nh-video-open + data-nh-video-src (+poster) ──
  document.querySelectorAll('[data-nh-video-open]').forEach(function (opener) {
    var src = opener.getAttribute('data-nh-video-src');
    if (!src) return;
    var poster = opener.getAttribute('data-nh-video-poster') || '';
    var inlineVideo = opener.querySelector('[data-nh-inline-video]');
    opener.addEventListener('click', function () {
      openModal(src, poster, opener, inlineVideo);
    });
    // Non-<button> openers (e.g. the UGC tile <div>) need keyboard support.
    if (opener.tagName !== 'BUTTON' && opener.tagName !== 'A') {
      if (!opener.hasAttribute('tabindex')) opener.setAttribute('tabindex', '0');
      if (!opener.hasAttribute('role')) opener.setAttribute('role', 'button');
      opener.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openModal(src, poster, opener, inlineVideo); }
      });
    }
  });

  // ── Inline muted autoplay (visible-only; reduced-motion/low-power → poster) ──
  document.querySelectorAll('[data-nh-inline-video]').forEach(function (video) {
    var tile = video.closest('[data-nh-inline-tile]');
    var source = video.querySelector('source[data-src]');
    var mark = function (on) { if (tile) tile.classList.toggle('nh-vid-playing', on); };
    if (reduceMotion || !('IntersectionObserver' in window)) { mark(false); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          if (source && !source.src) { source.src = source.dataset.src; video.load(); }
          video.muted = true;                 // set muted BEFORE play() (iOS)
          var p = video.play();
          if (p && p.then) { p.then(function () { mark(true); }).catch(function () { mark(false); }); }
          else { mark(true); }
        } else {
          video.pause();
        }
      });
    }, { threshold: 0.25 });
    io.observe(video);
  });
})();

// =============================================================================
// Section 23 — NH Cart Social Proof rotator (sections/nh-cart-social-proof.liquid)
// =============================================================================
// Crossfades one review at a time every 5s (opacity via .is-active). Respects
// prefers-reduced-motion (no rotation — first slide stays shown). No layout
// shift (slides are grid-stacked in one cell). Vanilla JS, scoped per section.
// =============================================================================
(function () {
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  document.querySelectorAll('[data-nh-cart-social]').forEach(function (root) {
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-nh-cs-slide]'));
    if (slides.length < 2 || reduce) return; // one slide (or reduced-motion) → static
    var i = 0;
    setInterval(function () {
      slides[i].classList.remove('is-active');
      i = (i + 1) % slides.length;
      slides[i].classList.add('is-active');
    }, 5000);
  });
})();
